/**
 * WebSocket 管理器 - 单例模式
 * 负责 WebSocket 连接、重连、心跳、消息缓存等核心功能
 * 整个应用生命周期内只存在一个实例
 */
class WebSocketManager {
  constructor(options = {}) {
    // 如果已经存在实例，返回现有实例（单例模式核心）
    if (WebSocketManager.instance) {
      console.log("WebSocketManager 存在")
      return WebSocketManager.instance
    }

    // 初始化配置
    this.url =
      options.url || process.env.VUE_APP_WS_TARGET || "ws://localhost:8080"
    this.reconnectInterval = options.reconnectInterval || 3000
    this.maxReconnectAttempts = options.maxReconnectAttempts || 10

    // 连接状态
    this.ws = null
    this.reconnectAttempts = 0
    this.shouldReconnect = true
    this.isManualDisconnect = false
    this._isConnecting = false

    // 定时器
    this.reconnectTimer = null
    this.stabilizeTimer = null  // 新增：稳定定时器

    // 事件监听器
    this.listeners = new Map()

    // 消息队列
    this.messageQueue = []
    this.maxQueueSize = 20

    // 保存实例
    WebSocketManager.instance = this

    // 心跳相关配置
    this.heartbeatInterval = options.heartbeatInterval || 30000  // 30秒
    this.heartbeatTimeout = options.heartbeatTimeout || 5000      // 5秒超时
    this.heartbeatTimer = null
    this.heartbeatTimeoutTimer = null
    this.lastPongTime = Date.now()
  }

  /**
   * 获取单例实例
   * @param {Object} options 配置选项
   * @returns {WebSocketManager} 单例实例
   */
  static getInstance(options = {}) {
    if (!WebSocketManager.instance) {
      WebSocketManager.instance = new WebSocketManager(options)
      console.log("创建新的 WebSocketManager 实例")
    } else {
      console.log("使用已有的 WebSocketManager 实例")
      if (options.url && options.url !== WebSocketManager.instance.url) {
        WebSocketManager.instance.url = options.url
      }
    }
    return WebSocketManager.instance
  }

  /**
   * 重新配置实例
   * @param {Object} options 新配置
   */
  reconfigure(options = {}) {
    const shouldReconnect = this.isConnected()
    let needReconnect = false

    if (options.url && options.url !== this.url) {
      this.url = options.url
      needReconnect = true
    }

    if (options.reconnectInterval) {
      this.reconnectInterval = options.reconnectInterval
    }

    if (options.maxReconnectAttempts) {
      this.maxReconnectAttempts = options.maxReconnectAttempts
    }

    // 如果 URL 改变且当前已连接，则重新连接
    if (needReconnect && shouldReconnect) {
      this.reconnect()
    }
  }

  /**
   * ✅ 新增：启动主动心跳
   */
  startHeartbeat() {
    this.stopHeartbeat()

    console.log('启动主动心跳，间隔:', this.heartbeatInterval, 'ms')

    this.heartbeatTimer = setInterval(() => {
      if (this.isConnected()) {
        console.log('发送主动 ping')

        // 发送 ping
        this.send('ping', {
          timestamp: Date.now(),
          type: 'heartbeat',
          messageId: new Date().getTime()

        }, false)  // false = 不缓存

        // 设置超时检测
        this.heartbeatTimeoutTimer = setTimeout(() => {
          console.warn('⏰ 心跳超时，未收到 pong 响应，准备重连...')
          this.heartbeatTimeoutTimer = null
          // 心跳超时，主动重连
          //this.reconnect()
          alert("断开连接，请刷新页面")
          window.location.reload()
        }, this.heartbeatTimeout)

      } else {
        console.warn('⚠️ 连接已断开，停止心跳')
        this.stopHeartbeat()
      }
    }, this.heartbeatInterval)
  }

  /**
  * ✅ 新增：停止主动心跳
  */
  stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
      console.log('🛑 心跳已停止')
    }
    if (this.heartbeatTimeoutTimer) {
      clearTimeout(this.heartbeatTimeoutTimer)
      this.heartbeatTimeoutTimer = null
    }
  }

  /**
 * ✅ 新增：处理 pong 响应
 */
  handlePong() {
    this.lastPongTime = Date.now()
    if (this.heartbeatTimeoutTimer) {
      clearTimeout(this.heartbeatTimeoutTimer)
      this.heartbeatTimeoutTimer = null
      console.log('收到 pong 响应，心跳正常')
    }
  }


  /**
   * 建立连接
   */
  connect() {
    // 防止重复连接
    if (this._isConnecting) {
      console.log("正在连接中，请稍候...")
      return
    }

    if (this.isConnected()) {
      console.log("已经连接，无需重复连接")
      return
    }

    // console.log('🎫 每次连接都需要新 ticket，触发获取')
    this.emit('need_ticket')
    return

    // if (this.reconnectAttempts > 0) {
    //   console.log(`🔄 重置重连计数 (之前: ${this.reconnectAttempts})`)
    //   this.reconnectAttempts = 0
    //   this.saveState()
    // }

    this.cleanupConnection()

    this._isConnecting = true
    this.isManualDisconnect = false
    this.shouldReconnect = true

    try {
      console.log(`正在连接到 WebSocket 服务器: ${this.url}`)
      this.ws = new WebSocket(this.url)
      this.bindEvents()
    } catch (error) {
      console.error("WebSocket connection error:", error)
      this._isConnecting = false
      this.handleReconnect()
    }
  }

  /**
 * ✅ 新增：使用新 ticket 建立连接（由 Store 调用）
 */
  connectWithNewTicket(ticket) {
    // 防止重复连接
    if (this._isConnecting) {
      console.log("正在连接中，请稍候...")
      return
    }

    if (this.isConnected()) {
      console.log("已经连接，无需重复连接")
      return
    }

    // 更新 URL
    this.updateTicket(ticket)

    this.cleanupConnection()
    this._isConnecting = true
    this.isManualDisconnect = false
    this.shouldReconnect = true

    try {
      console.log(`正在连接到 WebSocket 服务器: ${this.url}`)
      this.ws = new WebSocket(this.url)
      this.bindEvents()
    } catch (error) {
      console.error("WebSocket connection error:", error)
      this._isConnecting = false
      this.handleReconnect()
    }
  }

  updateTicket(ticket) {
    try {
      const urlObj = new URL(this.url)
      urlObj.searchParams.set('ticket', ticket)
      this.url = urlObj.toString()
      console.log('✅ Ticket 已更新')
      return true
    } catch (error) {
      console.error('更新 ticket 失败:', error)
      return false
    }
  }

  /**
   * 重新连接
   */
  reconnect() {
    console.log("🔄 手动触发重连")
    this.cleanupConnection()
    this.connect()
  }

  /**
   * 绑定 WebSocket 事件
   */
  bindEvents() {
    if (!this.ws) return

    this.ws.onopen = event => {
      console.log("WebSocket 已连接")
      console.log("当前队列长度:", this.messageQueue.length)
      this._isConnecting = false
      this.isManualDisconnect = false

      // ✅ 重置重连计数
      if (this.reconnectAttempts > 0) {
        console.log(`🔄 重置重连计数 (之前: ${this.reconnectAttempts})`)
        this.reconnectAttempts = 0
        this.saveState()
      }

      // ✅ 启动主动心跳
      this.startHeartbeat()

      // 清理稳定定时器
      if (this.stabilizeTimer) {
        clearTimeout(this.stabilizeTimer)
        this.stabilizeTimer = null
      }

      // 延迟重置重连计数，等待连接稳定
      this.stabilizeTimer = setTimeout(() => {
        if (this.isConnected() && this.reconnectAttempts !== 0) {
          console.log(`连接已稳定，重置重连计数 (之前: ${this.reconnectAttempts})`)
          this.reconnectAttempts = 0
          this.saveState()
        }
      }, 5000)

      this.saveState()
      this.sendQueuedMessages()
      console.log("sendQueuedMessages 调用完成!!!")
      this.emit("open", { timestamp: Date.now(), event })
    }

    this.ws.onclose = event => {
      console.log(`🔴 WebSocket 连接关闭: [${event.code}] ${event.reason || '无原因'}`)

      // ✅ 停止心跳
      this.stopHeartbeat()

      if (this.stabilizeTimer) {
        clearTimeout(this.stabilizeTimer)
        this.stabilizeTimer = null
      }
      this._isConnecting = false
      this.emit("close", event)

      if (!this.isManualDisconnect && this.shouldReconnect) {
        // 检查是否达到最大重连次数
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
          console.error(`❌ 达到最大重连次数 (${this.maxReconnectAttempts})，停止重连`)
          this.emit("max_reconnect_attempts")
          this.shouldReconnect = false
          return
        }
        this.handleReconnect()
      }
    }

    this.ws.onerror = error => {
      console.error("WebSocket 错误:", error)
      this._isConnecting = false
      this.emit("error", error)
    }

    this.ws.onmessage = event => {
      try {
        console.log("[原始WebSocket] 收到消息:", event.data)
        const data = JSON.parse(event.data)
        if (data.event == 'pong') { //前端主动心跳
          this.handlePong()
          return
        }

        this.emit("message", data)
        if (data.type) {
          this.emit(data.type, data.payload)
        }
      } catch (error) {
        console.log(
          "[WebSocketManager] JSON 解析失败，尝试修复:",
          error.message
        )
        // 如果不是 JSON 格式，作为原始消息处理
        this.emit("raw_message", event.data)
      }
    }
  }

  /**
   * 处理重连
   */
  handleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error("达到最大重连次数，停止重连")
      this.emit("max_reconnect_attempts")
      this.shouldReconnect = false
      return
    }

    this.reconnectAttempts++
    this.saveState()

    const delay = this.getReconnectDelay()
    console.log(`🔄 第 ${this.reconnectAttempts}/${this.maxReconnectAttempts} 次重连，延迟 ${delay}ms`)

    this.emit("reconnecting", {
      attempt: this.reconnectAttempts,
      max: this.maxReconnectAttempts,
      delay
    })


    //清理旧定时器
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }

    this.reconnectTimer = setTimeout(() => {
      console.log("正在尝试重新连接...")
      this.cleanupConnection()
      this.connect()
    }, delay)
  }

  /**
   * 获取重连延迟（带指数退避）
   */
  getReconnectDelay() {
    // 指数退避：1000, 2000, 4000, 8000, ...
    const baseDelay = Math.min(
      this.reconnectInterval * Math.pow(2, this.reconnectAttempts - 1),
      30000 // 最大延迟30秒
    )
    // 添加随机抖动，避免同时重连
    return baseDelay + Math.random() * 1000
  }

  /**
   * 发送消息
   * @param {string} type 消息类型
   * @param {any} payload 消息内容
   * @param {boolean} queueIfDisconnected 如果未连接是否加入队列
   */
  send(action, payload, queueIfDisconnected = true) {
    let message = null
    if (payload._data) {
      message = JSON.stringify(payload._data)
    } else {
      message = JSON.stringify({ action, ...payload })
    }

    // 直接从单例获取实例
    //const manager = WebSocketManager.getInstance()
    // console.log("manager 实例:", !!manager)

    // 详细检查连接状态
    //const isConnected = manager.isConnected()
    // console.log("manager.isConnected():", isConnected)
    // console.log("manager.ws:", manager.ws)
    // console.log("manager.ws.readyState:", manager.ws?.readyState)
    // console.log("WebSocket.OPEN:", WebSocket.OPEN)

    if (this.isConnected()) {
      try {
        this.ws.send(message)
        return true
      } catch (error) {
        console.error("发送消息失败:", error)
        if (queueIfDisconnected) {
          this.queueMessage(action, payload)
        }
        return false
      }
    } else {
      if (queueIfDisconnected) {
        this.queueMessage(action, payload)
        console.warn("WebSocket 未连接，消息已加入队列")
      }
      return false
    }
  }

  /**
   * 消息入队
   */
  queueMessage(type, payload) {
    this.messageQueue.push({
      type,
      payload,
      timestamp: Date.now(),
      id: this.generateMessageId()
    })

    // 限制队列大小
    if (this.messageQueue.length > this.maxQueueSize) {
      const removed = this.messageQueue.shift()
      console.warn("消息队列已满，移除旧消息:", removed)
    }

    // 保存到 storage
    this.saveMessageQueue()
  }

  /**
   * 发送队列中的消息
   */
  sendQueuedMessages() {
    if (this.messageQueue.length === 0) return

    console.log(`开始发送队列中的 ${this.messageQueue.length} 条消息`)

    const messagesToSend = [...this.messageQueue]
    this.messageQueue = []
    this.saveMessageQueue()

    messagesToSend.forEach(msg => {
      setTimeout(() => {
        this.send(msg.type, msg.payload, true)
      }, 100) // 稍微延迟，避免阻塞
    })
  }

  /**
   * 保存消息队列到 storage
   */
  saveMessageQueue() {
    try {
      sessionStorage.setItem(
        "ws_message_queue",
        JSON.stringify(this.messageQueue)
      )
    } catch (e) {
      console.error("保存消息队列失败:", e)
    }
  }

  /**
   * 加载消息队列从 storage
   */
  loadMessageQueue() {
    try {
      const saved = sessionStorage.getItem("ws_message_queue")
      if (saved) {
        this.messageQueue = JSON.parse(saved)
      }
    } catch (e) {
      console.error("加载消息队列失败:", e)
    }
  }

  /**
   * 生成消息 ID
   */
  generateMessageId() {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  /**
   * 添加事件监听
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }
    this.listeners.get(event).add(callback)
  }

  /**
   * 移除事件监听
   */
  off(event, callback) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).delete(callback)
    }
  }

  /**
   * 触发事件
   */
  emit(event, data) {
    console.log(`[WS-Manager] 触发事件: ${event}`, {
      hasListeners: this.listeners.has(event),
      listenerCount: this.listeners.has(event)
        ? this.listeners.get(event).size
        : 0,
      allEvents: Array.from(this.listeners.keys()),
      data: data
    })

    if (this.listeners.has(event)) {
      const listeners = Array.from(this.listeners.get(event))
      console.log(`[WS-Manager] ${event} 的监听器列表:`, listeners.length)

      listeners.forEach((callback, index) => {
        console.log(`[WS-Manager] 执行第 ${index + 1} 个 ${event} 监听器`)
        try {
          callback(data)
        } catch (error) {
          console.error(`[WS-Manager] 监听器执行失败:`, error)
        }
      })
    }
  }

  /**
   * 保存状态到 storage
   */
  saveState() {
    try {
      const state = {
        reconnectAttempts: this.reconnectAttempts,
        url: this.url,
        timestamp: Date.now()
      }
      sessionStorage.setItem("ws_manager_state", JSON.stringify(state))
    } catch (e) {
      console.error("保存状态失败:", e)
    }
  }

  /**
   * 从 storage 恢复状态
   */
  loadState() {
    try {
      const saved = sessionStorage.getItem("ws_manager_state")
      if (saved) {
        const state = JSON.parse(saved)
        // 只恢复重连尝试次数，如果时间不太久
        if (Date.now() - state.timestamp < 60000) {
          // 1分钟内
          this.reconnectAttempts = state.reconnectAttempts
        }
      }
    } catch (e) {
      console.error("加载状态失败:", e)
    }
  }

  /**
   * 获取连接状态
   */
  getReadyState() {
    return this.ws ? this.ws.readyState : WebSocket.CLOSED
  }

  /**
   * 是否已连接
   */
  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN
  }

  /**
   * 是否正在连接
   */
  isConnecting() {
    return this.ws && this.ws.readyState === WebSocket.CONNECTING
  }

  /**
   * 获取当前连接信息
   */
  getConnectionInfo() {
    return {
      url: this.url,
      readyState: this.getReadyState(),
      isConnected: this.isConnected(),
      isConnecting: this.isConnecting(),
      reconnectAttempts: this.reconnectAttempts,
      queueSize: this.messageQueue.length
    }
  }

  /**
   * 手动断开连接
   * @param {boolean} permanent 是否永久断开（不再重连）
   */
  disconnect(permanent = true) {
    this.isManualDisconnect = true
    this.shouldReconnect = !permanent
    this._isConnecting = false

    // ✅ 修改：使用统一的清理方法
    this.cleanupConnection()

    if (permanent) {
      this.clearState()
    }

    console.log("WebSocket 已断开" + (permanent ? "（永久）" : "（临时）"))
  }

  /**
   * 清除状态
   */
  clearState() {
    this.reconnectAttempts = 0
    this.messageQueue = []
    sessionStorage.removeItem("ws_manager_state")
    sessionStorage.removeItem("ws_message_queue")
  }

  cleanupConnection() {
    console.log("🧹 清理连接资源")
    // ✅ 停止心跳
    this.stopHeartbeat()

    // 清理定时器
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }

    if (this.stabilizeTimer) {
      clearTimeout(this.stabilizeTimer)
      this.stabilizeTimer = null
    }

    // 清理 WebSocket
    if (this.ws) {
      this.ws.onopen = null
      this.ws.onclose = null
      this.ws.onmessage = null
      this.ws.onerror = null

      try {
        if (this.ws.readyState !== WebSocket.CLOSED) {
          this.ws.close()
        }
      } catch (error) {
        console.error("关闭连接时出错:", error)
      }

      this.ws = null
    }

    this._isConnecting = false
  }

  /**
   * 销毁实例（用于登出等场景）
   */
  static destroyInstance() {
    if (WebSocketManager.instance) {
      WebSocketManager.instance.disconnect(true)
      WebSocketManager.instance.listeners.clear()
      WebSocketManager.instance = null
      console.log("WebSocketManager 单例已销毁")
    }
  }

  /**
   * 重置实例（重新初始化）
   */
  static resetInstance(options = {}) {
    WebSocketManager.destroyInstance()
    return WebSocketManager.getInstance(options)
  }
}

export default WebSocketManager
